/**
 * SE2 · fragrance PDP purchase options + in-button price (Lane 5, 2026-09-09; rebuilt 2026-09-22).
 *
 * 1. <se2-purchase-options> (markup: snippets/se2-pdp-purchase-options.liquid, rendered inside the product form by
 *    blocks/buy-buttons.liquid when the product has selling plans): the radio choice enables the chosen tier's
 *    <select name="selling_plan"> and disables the others, so exactly one plan (or none, for One-time) is submitted.
 *    The segment labels ([data-se2-seg], Figma 625:75) mirror the checked radio, and the in-button price
 *    (.se2-buy__atc-price[data-se2-atc-price]) shows the chosen option's price.
 * 2. The offer is READ outside the panel: the section draws the price pair under the tags
 *    (sections/se2-fragrance-pdp.liquid — [data-se2-active-price], [data-se2-active-compare]) and the form draws
 *    the plan line and the benefits line in a slot after the size tiles and before the toggle
 *    (blocks/buy-buttons.liquid — [data-se2-plan-line] with the badge and the "← 30 days →" stepper,
 *    [data-se2-plan-benefits] with the chips; there since the 2026-09-25 audit, so that picking a subscription
 *    size reveals them below the tiles instead of pushing the tiles down). This module writes all of it from the
 *    settings handed over on the panel element, on every change, and the stepper is delegated from the section
 *    root back into the panel: it moves the <select>. One source of truth — the select — and one place a price
 *    is stated.
 * 3. On a variant change Horizon (assets/product-form.js) morphs only the add-to-cart button, the quantity label/rules
 *    and the price block; the rest of .product-form-buttons is left as rendered. So after the standard product:select
 *    promise resolves, this module replaces the in-button price and the purchase-options panel from the fetched section
 *    HTML, after the theme's own handlers (same promise, then a macrotask), carrying the shopper's choice across. The
 *    fresh panel rewrites the section's row on connect, so the offer follows the variant too.
 * 4. INSTANT PAINT (2026-09-24). Step 3 only lands when Horizon's section re-fetch does, and on a slow connection
 *    that is 3–6 seconds after the tap — measured on production: 50 mL → One-time → 120 mL still read $39.99 on the
 *    price and the button 2.5 s later. The section now embeds a price map (every variant's one-time price and its
 *    selling-plan prices, [data-se2-price-map]); on the size radio's change event, before Horizon has even started
 *    its fetch, this module paints the pair and the button from that map, sets the form's variant id, and makes
 *    sure no selling plan the new variant cannot honour is left enabled. The re-fetch then reconciles as before.
 *
 * 5. AUDIT 2026-09-29.
 *    - Sold out (FPDP-B-02 / DPDP-B-02): a size that cannot be bought paints its one-time price with a "Sold out" tag,
 *      hides the toggle slot, the note and the plan slot, and never writes the in-button price; after the re-fetch the
 *      three are dropped or re-inserted to match the server, and the #se2-restock next step and the reassurance row
 *      (whose "Ships in 1 day" is a stock promise) are swapped from the fetched HTML.
 *    - Which price (FPDP-M-05): "Subscribe & save" after the plan price and "one-time" after the struck price while a
 *      plan is checked, and the button reads "Subscribe · $39.99" ([data-se2-atc-label]); One-time reads "Add to cart".
 *      A MutationObserver re-applies the label after Horizon morphs the button, before the next paint.
 *    - Announced (FPDP-B-17): one sentence per size or option change in the section's [data-se2-price-live],
 *      debounced 400ms and written only when it differs, so the instant paint and the re-fetch announce once.
 *    - The bigger-bottle line under the tiles ([data-se2-size-upsell], FPDP-B-13) selects its size by clicking the radio,
 *      and so does "Switch to 120 mL" under a sold-out size ([data-se2-restock-switch]), in place of reloading the page.
 *    - The diffuser head's "Sold out" tag (DPDP-B-02 part 5, 2026-10-05) follows the colour: from the tapped swatch's
 *      data-option-available at once, then from the fetched variant. Only inside [data-se2-dpdp3].
 *
 * 6. REDESIGN (2026-11-04, batch 3; OilProposed boards, b345 contract).
 *    - The plan picker is two stacked cards (snippets/se2-pdp-purchase-options.liquid). The cards' radios and selects are
 *      the same inputs as before, so steps 1 to 5 hold; the toggle segments and the "← 30 days →" stepper are gone, and
 *      the frequency is a small select inside the Subscribe card when a size has more than one plan.
 *    - The price is two lines, never a strike (P2): "$31.92 every 30 days" over "or $39.90 one-time", or "$39.90" over
 *      "or $31.92 every 30 days with Subscribe & save" (paintPriceLines). The words come from the price row's
 *      data-se2-w-* attributes and every figure from the price map or the chosen <option>.
 *    - Plan state: the form's selling_plan input is the truth. A change made on a card dispatches
 *      se2:plan-change { productId, sellingPlanId, source: 'plan-picker' }; a change from any other control (the sticky
 *      bar's switch) is applied to the cards from the event, without dispatching again.
 *    - Motion: [data-se2-rise] lists (Smells like, the composition tiles, the kit card's FREE tag) rise in turn once
 *      when they first come into view, only under html.se2-motion, never with reduced motion (armRise).
 *
 * 7. SALE PRICING (2026-11-04; snippets/se2-sale.liquid). While an automatic discount is live, the price map and every
 *    plan card and option already carry the price the cart gives (data-se2-price, the map's "price"), with the regular
 *    price beside it (data-se2-price-base, "base") and data-se2-sale="1" / "sale": true on a price that is on sale. So
 *    every line above follows the sale with no extra work; this module only shows the chocolate tag, the screen-reader
 *    "Sale price, was …" and the end line beside the price while the CHOSEN size and plan are on sale (paintSale), and
 *    each card's own tag (paintOptionSale). A one-time-only sale shows no tag on Subscribe & save. When
 *    assets/se2-sale.js puts the page back to its regular prices (se2:sale-end), the maps are read again and every
 *    panel repaints. Never a crossed-out price.
 *
 * Loaded as a module by blocks/buy-buttons.liquid when the block setting se2_po_enable is on (the fragrance template),
 * and on any product with a sold-out variant (for the next step and the reassurance row).
 * Imports nothing from the theme except the Shopify standard event names already mapped in the import map.
 */
import { StandardEvents } from '@shopify/events';

/** Remembered choice per buy-buttons block id, survives the panel swap on variant change. */
const CHOICES = new Map();

/**
 * One live-region sentence from the region's data-se2-msg-* template (sections/se2-fragrance-pdp.liquid prints them
 * from locales/*.json, se2_pdp.live_*), with [size] / [price] / [cadence] filled in; the English is the fallback.
 * @param {Element | null | undefined} root
 * @param {string} name dataset key
 * @param {string} fallback
 * @param {Record<string, string>} [vars]
 */
function liveWords(root, name, fallback, vars = {}) {
  const region = root ? liveRegionIn(root) : null;
  const tpl = (region instanceof HTMLElement && region.dataset[name]) || fallback;
  return tpl.replace(/\[(\w+)\]/g, (match, k) => (k in vars ? String(vars[k]) : match));
}

/**
 * Live regions whose page has been interacted with (or whose load-time state is recorded), so the page's first
 * paint is never announced but every later change is. Keyed by the region, because the panel reaches it from the
 * section's .se2-fpdp3 root and the size change from the .shopify-section around it.
 * @type {WeakSet<Element>}
 */
const PRIMED = new WeakSet();

/** @param {Element} root */
function liveRegionIn(root) {
  return root.querySelector('[data-se2-price-live]');
}

/**
 * Marks the page as interacted with; returns true when this is the first call for its region (the load state).
 * @param {Element} root
 */
function markPrimed(root) {
  const region = liveRegionIn(root);
  if (!region) return false;
  const first = !PRIMED.has(region);
  PRIMED.add(region);
  return first;
}

/**
 * Whether the shopper arrived on an Offer URL — ?variant=, the per-size URL the product's structured data hands
 * to Google. Read once, here, because assets/variant-picker.js rewrites ?variant= on every size change. The
 * markup carries the one-time price and Google compares it with the price the landing page shows after scripts
 * run, so such a landing defaults to One-time even where the merchant's default is the plan; a choice the
 * shopper has already made still wins, and Subscribe & save stays one tap away (audit 2026-09-25,
 * offer-price-vs-subscribe-default). The 20% rate and the sizes that carry it are untouched.
 */
const SE2_LANDED_ON_OFFER_URL = new URLSearchParams(window.location.search).has('variant');

class Se2PurchaseOptions extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;

  connectedCallback() {
    // The panel owns the plan from here: an interim selling_plan input the instant paint put in the form while the
    // panel was still in flight (see paintVariant) is withdrawn before the panel's own <select> is enabled.
    for (const interim of this.#form?.querySelectorAll('input[data-se2-plan-interim]') ?? []) interim.remove();
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('click', this.#onClick, { signal });
    // The plan line lives in the section, outside the form; its stepper is delegated from the root so the
    // handler survives the panel being replaced on a variant change.
    const root = this.#root;
    if (root && root !== this) root.addEventListener('click', this.#onRootClick, { signal });
    // The benefits row only draws its scroll hairline when it overflows, which changes with the viewport.
    window.addEventListener('resize', this.#onResize, { signal });

    // Another control (the sticky bar's switch) changed the plan: follow it, never answer it (b345 contract).
    document.addEventListener('se2:plan-change', this.#onPlanChange, { signal });

    const saved = CHOICES.get(this.#key);
    if (saved) this.#restore(saved);
    else this.#applyDefault();
    this.#enhance();
    // The first connect on a page is its load state: recorded for the live region, not announced. Every later
    // connect (the fresh panel after a size change) announces, and the de-duplication keeps it to one sentence.
    this.#apply(markPrimed(root));
    // The default (or the remembered choice) may have changed the plan the server printed, so the other controls
    // are told what the form now posts.
    this.#dispatch();
  }

  /** se2:plan-change with the plan the form now posts (null for One-time). */
  #dispatch() {
    const select = this.#activeSelect();
    document.dispatchEvent(
      new CustomEvent('se2:plan-change', {
        detail: {
          productId: this.dataset.productId || '',
          sellingPlanId: select && !select.disabled && select.value ? select.value : null,
          source: 'plan-picker',
        },
      }),
    );
  }

  /** @param {Event} event */
  #onPlanChange = (event) => {
    const detail = event instanceof CustomEvent ? event.detail || {} : {};
    // Its own events, and the size change's (paintVariant): that one reports what a new size posts, which must not
    // overwrite the shopper's remembered choice on the old cards while the new ones are in flight.
    if (detail.source === 'plan-picker' || detail.source === 'size') return;
    if (detail.productId && this.dataset.productId && String(detail.productId) !== String(this.dataset.productId)) return;
    const want = detail.sellingPlanId == null || detail.sellingPlanId === '' ? null : String(detail.sellingPlanId);
    /** @type {HTMLInputElement | null} */
    let radio = null;
    if (want) {
      const option = this.querySelector(`select[data-se2-plan-select] option[value="${CSS.escape(want)}"]`);
      const select = option?.closest('select');
      if (!(select instanceof HTMLSelectElement)) return;
      select.value = want;
      radio = select.closest('[data-se2-option]')?.querySelector('[data-se2-plan-radio]') ?? null;
    } else {
      radio = this.querySelector('[data-se2-once-radio]');
    }
    if (!(radio instanceof HTMLInputElement)) return;
    radio.checked = true;
    markPrimed(this.#root);
    this.#apply();
  };

  disconnectedCallback() {
    this.#abort?.abort();
  }

  /**
   * Repaints from the cards' data as it is now (se2:sale-end: assets/se2-sale.js has put the regular prices back).
   * @param {boolean} [announce] say the new price in the live region
   */
  refresh(announce = false) {
    this.#apply(!announce);
  }

  /** The section around the form — where the price pair and the plan line are drawn. */
  get #root() {
    return this.closest('.se2-fpdp3, .se2-dpdp3, .shopify-section') ?? this;
  }

  get #key() {
    return this.dataset.formBlock || 'default';
  }

  get #form() {
    return this.closest('form');
  }

  /**
   * Marks the panel enhanced: the stylesheet then hides the option panels (the native <select> and the
   * <details> copy of the benefits), because the section's lines have taken over. Done here rather than in
   * Liquid so the panel is the whole control until this script runs: without JavaScript a shopper still gets
   * a working frequency picker and the benefits list.
   */
  #enhance() {
    this.classList.add('is-enhanced');
    for (const wrap of this.querySelectorAll('[data-se2-freq-wrap]')) wrap.classList.add('is-enhanced');
  }

  /**
   * Applies the merchant's `se2_po_default` on first connect.
   * The snippet renders One-time checked so that a page without JavaScript posts no selling_plan; the
   * "default to subscription" preference is therefore applied here rather than in Liquid. A tier that is
   * already checked (a ?selling_plan= deep link) is left alone, and so is a landing on an Offer URL
   * (SE2_LANDED_ON_OFFER_URL), which keeps the rendered One-time.
   */
  #applyDefault() {
    if (this.dataset.default !== 'plan' || SE2_LANDED_ON_OFFER_URL) return;
    if (this.querySelector('input[type="radio"]:checked[data-se2-plan-radio]')) return;
    const radio = this.querySelector('[data-se2-plan-radio]');
    if (radio instanceof HTMLInputElement) radio.checked = true;
  }

  /**
   * Re-selects the remembered option on a freshly rendered panel.
   * @param {{ mode: 'plan' | 'once', plan: string | null }} saved
   */
  #restore(saved) {
    /** @type {HTMLInputElement | null} */
    let radio = null;

    if (saved.mode === 'once') {
      radio = this.querySelector('[data-se2-once-radio]');
    } else {
      if (saved.plan) {
        const option = this.querySelector(`select[data-se2-plan-select] option[value="${CSS.escape(saved.plan)}"]`);
        const select = option?.closest('select');
        if (select instanceof HTMLSelectElement) {
          select.value = saved.plan;
          radio = select.closest('[data-se2-option]')?.querySelector('[data-se2-plan-radio]') ?? null;
        }
      }
      radio = radio || this.querySelector('[data-se2-plan-radio]');
    }

    if (radio instanceof HTMLInputElement) radio.checked = true;
  }

  /** The chosen tier's plan <select>, or null while One-time is chosen. */
  #activeSelect() {
    const checked = this.querySelector('input[type="radio"]:checked');
    const option = checked?.closest('[data-se2-option]');
    const select = option?.querySelector('select[data-se2-plan-select]');
    return select instanceof HTMLSelectElement ? select : null;
  }

  /**
   * "← 30 days →": moves the chosen tier's <select> one plan along, wrapping at either end. Pressed while
   * One-time is chosen, it picks the first plan first — the arrows are only drawn in plan mode, but a
   * keyboard can still reach them.
   * @param {number} direction
   */
  #stepFrequency(direction) {
    let select = this.#activeSelect();
    if (!select) {
      const radio = this.querySelector('[data-se2-plan-radio]');
      if (radio instanceof HTMLInputElement) radio.checked = true;
      select = this.#activeSelect();
      if (!select) return;
    }
    const count = select.options.length;
    if (count > 1) select.selectedIndex = (select.selectedIndex + direction + count) % count;
    this.#apply();
    this.#dispatch();
  }

  /**
   * The benefits row under the plan line: one chip per bullet the merchant wrote, with "[percent]" filled in,
   * and a bullet that quotes the discount left out when there is none. Rebuilt only when its text would change,
   * so stepping the frequency does not throw away a scroll position.
   * @param {Element} root
   * @param {number} percent
   * @param {boolean} hasDiscount
   * @param {boolean} show
   */
  #renderBenefits(root, percent, hasDiscount, show) {
    const block = root.querySelector('[data-se2-plan-benefits]');
    if (!(block instanceof HTMLElement)) return;

    // getAttribute, not dataset: the DOM camel-cases a hyphen only before a letter, so `data-bullet-1` is
    // dataset["bullet-1"], never dataset.bullet1 — which is how the row first rendered empty.
    const bullets = ['data-bullet-1', 'data-bullet-2', 'data-bullet-3']
      .map((name) => (this.getAttribute(name) || '').trim())
      .filter((text) => text && (hasDiscount || !text.includes('[percent]')))
      .map((text) => text.replace('[percent]', `${percent}%`));

    // Two distinct benefits or none (audit 2026-09-29, FPDP-M-08): a single chip only repeated the segment label.
    block.hidden = !show || bullets.length < 2;
    if (block.hidden) return;

    const label = block.querySelector('[data-se2-plan-benefits-label]');
    if (label instanceof HTMLElement) {
      const text = this.dataset.benefitsLabel || '';
      label.textContent = text;
      label.hidden = text === '';
    }

    const track = block.querySelector('[data-se2-plan-benefits-track]');
    if (!(track instanceof HTMLElement)) return;
    const key = bullets.join('\u0001');
    if (track.dataset.se2Rendered !== key) {
      track.textContent = '';
      bullets.forEach((text, index) => {
        const item = document.createElement('li');
        item.className = 'se2-fpdp3__plan-benefit';
        // Each chip carries its index so the stylesheet can stagger the rise, first to last.
        item.style.setProperty('--i', String(index));
        item.insertAdjacentHTML(
          'afterbegin',
          '',
        );
        item.appendChild(document.createTextNode(text));
        track.appendChild(item);
      });
      track.dataset.se2Rendered = key;
      // Two frames on, so the chips' resting styles have painted before the class that transitions them in.
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          for (const chip of track.children) chip.classList.add('is-in');
        }),
      );
      // The row now has content the motion script could not see at load: on a phone it slides the chips
      // 36px and back once, the same hint every other rail on the site gives.
      document.dispatchEvent(new CustomEvent('se2:rail'));
    }
    this.#syncBenefitsScroll(block);
  }

  /** The hairline under the row exists only when the row actually overflows. */
  #syncBenefitsScroll(block) {
    const target = block ?? this.#root.querySelector('[data-se2-plan-benefits]');
    if (!(target instanceof HTMLElement) || target.hidden) return;
    const track = target.querySelector('[data-se2-plan-benefits-track]');
    if (!(track instanceof HTMLElement)) return;
    target.classList.toggle('is-scrollable', track.scrollWidth > track.clientWidth + 1);
  }

  #onResize = () => this.#syncBenefitsScroll();

  #onRootClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const step = target.closest('[data-se2-plan-step]');
    if (step instanceof HTMLElement) {
      markPrimed(this.#root);
      this.#stepFrequency(Number(step.dataset.se2PlanStep) || 1);
    }
  };

  /** Clicking anywhere on an option (not on a control inside it) selects it. */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.closest('select, option, a, button, input, label, summary, details')) return;

    const option = target.closest('[data-se2-option]');
    if (!option || !this.contains(option)) return;

    const radio = option.querySelector('input[type="radio"]');
    if (radio instanceof HTMLInputElement && !radio.checked) {
      radio.checked = true;
      markPrimed(this.#root);
      this.#apply();
      this.#dispatch();
    }
  };

  #onChange = () => {
    markPrimed(this.#root);
    this.#apply();
    this.#dispatch();
  };

  /** @param {boolean} [prime] record the state for the live region without announcing it (page load) */
  #apply(prime = false) {
    const root = this.#root;
    const checked = this.querySelector('input[type="radio"]:checked');
    const options = Array.from(this.querySelectorAll('[data-se2-option]'));

    /** @type {string | null} */
    let activePrice = null;
    /** @type {string | null} */
    let activePlan = null;
    let activeDiscount = false;
    let activePercent = 0;
    let activeBare = '';
    let activeEvery = '';
    /** The first Subscribe card's chosen plan, quoted under the price while One-time is chosen. */
    let offerPrice = '';
    let offerEvery = '';
    /** @type {string | null} */
    let oncePrice = null;
    /** Sale pricing: whether the chosen option's price is a sale price, and its regular price. */
    let activeSale = false;
    let activeBase = '';
    const cadenceTemplate = this.dataset.cadenceTemplate || '';

    for (const option of options) {
      const radio = option.querySelector('input[type="radio"]');
      const active = radio === checked;
      option.classList.toggle('is-selected', active);

      const select = option.querySelector('select[data-se2-plan-select]');

      if (select instanceof HTMLSelectElement) {
        select.disabled = !active;

        const chosen = select.options[select.selectedIndex];
        const planPrice = chosen?.dataset.se2Price || option.dataset.se2Price || null;
        const percent = Number(chosen?.dataset.se2Percent || 0);
        const hasDiscount = percent > 0;

        const bullet = option.querySelector('[data-se2-bullet-percent]');
        if (bullet instanceof HTMLElement) {
          // the text span carries the template; its row (check glyph + text) is what hides
          const row = bullet.closest('li') ?? bullet;
          row.hidden = !hasDiscount;
          if (hasDiscount) {
            bullet.textContent = (bullet.dataset.se2BulletTemplate || '').replace('[percent]', `${percent}%`);
          }
        }

        const bare = chosen?.dataset.se2FreqBare || chosen?.dataset.se2FreqShort || '';
        const every = chosen?.dataset.se2FreqShort || '';
        const planSale = chosen?.dataset.se2Sale === '1';
        const planBase = chosen?.dataset.se2PriceBase || option.dataset.se2PriceBase || '';
        paintOptionSale(option, planSale, planBase);
        // The card's own lines follow its select: the price and "120 mL, delivered every 45 days".
        const cardPrice = option.querySelector('[data-se2-po-price]');
        if (cardPrice && planPrice) cardPrice.textContent = planPrice;
        const cardCadence = option.querySelector('[data-se2-po-cadence]');
        if (cardCadence && cadenceTemplate && bare) cardCadence.textContent = cadenceTemplate.replace('[every]', bare);
        if (!offerPrice && planPrice) {
          offerPrice = planPrice;
          offerEvery = every;
        }

        if (active) {
          activePrice = planPrice;
          activePlan = select.value || null;
          activeDiscount = hasDiscount;
          activePercent = percent;
          activeBare = bare;
          activeEvery = every;
          activeSale = planSale;
          activeBase = planBase;
        }
      } else {
        // the One-time option: its price is the struck price beside any discounted plan price
        oncePrice = option.dataset.se2Price || oncePrice;
        // The card's own price follows its data too (sale pricing: the regular price comes back when a sale ends).
        const onceCardPrice = option.querySelector('[data-se2-po-price]');
        if (onceCardPrice && option.dataset.se2Price && onceCardPrice.textContent !== option.dataset.se2Price) {
          onceCardPrice.textContent = option.dataset.se2Price;
        }
        const onceSale = option.dataset.se2Sale === '1';
        paintOptionSale(option, onceSale, option.dataset.se2PriceBase || '');
        if (active) {
          activePrice = option.dataset.se2Price || null;
          activeDiscount = false;
          activeSale = onceSale;
          activeBase = option.dataset.se2PriceBase || '';
        }
      }
    }

    // segment labels mirror the checked radio (CSS :has covers the single-tier, no-JS case)
    const checkedId = checked instanceof HTMLInputElement ? checked.id : '';
    for (const seg of this.querySelectorAll('[data-se2-seg]')) {
      seg.classList.toggle('is-selected', checkedId !== '' && seg.getAttribute('for') === checkedId);
    }

    const mode = checked instanceof HTMLElement && checked.hasAttribute('data-se2-once-radio') ? 'once' : 'plan';
    CHOICES.set(this.#key, { mode, plan: activePlan });

    // ---- the price lines (P2: two lines, no strike) ----
    if (activePrice) {
      paintPriceLines(root, {
        price: activePrice,
        every: mode === 'plan' ? activeEvery : '',
        meta:
          mode === 'plan'
            ? priceWords(root, 'se2WOrOnce', 'or [price] one-time', { price: oncePrice || '' })
            : offerPrice
              ? priceWords(root, 'se2WOrPlan', 'or [price] [every] with Subscribe\u00a0&\u00a0save', {
                  price: offerPrice,
                  every: offerEvery,
                })
              : priceWords(root, 'se2WOnceOnly', 'One-time only'),
      });
    }

    // ---- sale pricing: the tag, the screen-reader sentence and the end line follow the chosen option ----
    if (activePrice) paintSale(root, activeSale, activeBase);

    // ---- the section's price pair (725:1193; the diffuser page and older markup) ----
    const price = root.querySelector('[data-se2-active-price]');
    if (price && activePrice) price.textContent = activePrice;

    const compare = root.querySelector('[data-se2-active-compare]');
    if (compare instanceof HTMLElement) {
      compare.hidden = !activeDiscount;
      const value = compare.querySelector('[data-se2-active-compare-value]');
      if (value && oncePrice) value.textContent = oncePrice;
    }
    // "Subscribe & save" after the plan price while a plan is checked (FPDP-M-05); a panel only exists on a size
    // that can be bought, so the sold-out tag is down whenever it applies.
    setOfferQualifier(root, mode === 'plan');
    setStockTag(root, true);

    // ---- the section's plan line: badge, "Delivery every ← 30 days →", spark ----
    // The cards of the redesign (.se2-pp) carry all of this themselves; the plan line is still written (the sticky
    // bar of 2026-10-05 reads its cadence) but stays hidden, and its benefit chips are not built.
    const cards = this.classList.contains('se2-pp');
    const line = root.querySelector('[data-se2-plan-line]');
    if (line instanceof HTMLElement) {
      line.hidden = mode !== 'plan';

      // An empty badge setting means no badge (audit 2026-09-29, FPDP-M-08): the segment already says 20%.
      const badge = line.querySelector('[data-se2-plan-badge]');
      if (badge instanceof HTMLElement) {
        const template = (this.getAttribute('data-badge-template') ?? '[percent] OFF').trim();
        badge.hidden = !activeDiscount || template === '';
        badge.textContent = badge.hidden ? '' : template.replace('[percent]', `${activePercent}%`);
      }

      const prefix = line.querySelector('[data-se2-plan-prefix]');
      if (prefix instanceof HTMLElement && this.dataset.deliveryPrefix) prefix.textContent = this.dataset.deliveryPrefix;

      const current = line.querySelector('[data-se2-plan-current]');
      if (current instanceof HTMLElement) current.textContent = activeBare;

      // arrows only when there is something to step through
      const select = this.#activeSelect();
      const many = !!select && select.options.length > 1;
      for (const arrow of line.querySelectorAll('[data-se2-plan-step]')) {
        if (arrow instanceof HTMLElement) arrow.hidden = !many;
      }
    }

    // ---- the section's benefits line ----
    if (!cards) this.#renderBenefits(root, activePercent, activeDiscount, mode === 'plan');

    setAtcPrice(this.#form, activePrice);
    setAtcLabel(this.#form, mode);

    // ---- one sentence for the live region (FPDP-B-17) ----
    if (activePrice) {
      announce(
        root,
        priceSentence({
          root,
          size: sizeLabel(root),
          available: true,
          plan: mode === 'plan',
          price: activePrice,
          once: oncePrice,
          cadence: activeBare,
        }),
        prime,
      );
    }
  }
}

/**
 * Writes the chosen option's price into the in-button price span of the same form.
 * @param {HTMLFormElement | null} form
 * @param {string | null} price
 */
function setAtcPrice(form, price) {
  if (!form || !price) return;
  const el = form.querySelector('[data-se2-atc-price]');
  if (el) el.textContent = price;
}

/**
 * One of the price row's words (data-se2-w-*, printed by sections/se2-fragrance-pdp.liquid from the locales), with
 * [price] and [every] filled in; the English is the fallback.
 * @param {Element} root
 * @param {string} name dataset key
 * @param {string} fallback
 * @param {Record<string, string>} [vars]
 */
function priceWords(root, name, fallback, vars = {}) {
  const row = root.querySelector('[data-se2-w-or-once]');
  const tpl = (row instanceof HTMLElement && row.dataset[name]) || fallback;
  return tpl.replace(/\[(\w+)\]/g, (match, k) => (k in vars ? String(vars[k]) : match)).replace(/\s+$/, '');
}

/**
 * The two price lines of the redesign (2026-11-04): the chosen price, its cadence on a plan, and the line under it.
 * @param {Element} root
 * @param {{ price: string, every: string, meta: string }} lines
 */
function paintPriceLines(root, { price, every, meta }) {
  const value = root.querySelector('[data-se2-active-price]');
  if (value && price) value.textContent = price;
  const cadence = root.querySelector('[data-se2-price-every]');
  if (cadence instanceof HTMLElement) {
    cadence.textContent = every ? ` ${every}` : '';
    cadence.hidden = !every;
  }
  const line = root.querySelector('[data-se2-price-meta]');
  if (line instanceof HTMLElement && line.textContent !== meta) line.textContent = meta;
}

/**
 * "Sale price, was $39.90" from an element's own template (data-se2-tpl, printed from se2_sale.was).
 * @param {Element} el
 * @param {string} base
 */
function wasSentence(el, base) {
  const tpl = el.getAttribute('data-se2-tpl') || 'Sale price, was [price]';
  return `. ${tpl.replace('[price]', base)}`;
}

/**
 * Sale pricing (2026-11-04): the chocolate tag beside the price, the screen-reader sentence after it and the
 * "Ends Monday, Nov 9" line, shown only while the chosen size and plan are on sale. Nothing once the page's sale has
 * ended ([data-se2-sale-over], assets/se2-sale.js), and nothing at all on a page without a sale (no such elements).
 * @param {Element} root
 * @param {boolean} on
 * @param {string} base the regular price
 */
function paintSale(root, on, base) {
  const block = root.querySelector('[data-se2-offer-price]');
  if (!block) return;
  const show = on && !block.closest('[data-se2-sale-over]');
  for (const el of block.querySelectorAll('[data-se2-sale-tag], [data-se2-sale-ends-line]')) {
    if (el instanceof HTMLElement) el.hidden = !show;
  }
  const was = block.querySelector('[data-se2-sale-was]');
  if (was instanceof HTMLElement) {
    was.hidden = !show;
    if (show && base) {
      const text = wasSentence(was, base);
      if (was.textContent !== text) was.textContent = text;
    }
  }
}

/**
 * A plan card's own tag and sentence (snippets/se2-pdp-purchase-options.liquid).
 * @param {Element} option [data-se2-option]
 * @param {boolean} on
 * @param {string} base
 */
function paintOptionSale(option, on, base) {
  const show = on && !option.closest('[data-se2-sale-over]');
  const tag = option.querySelector('[data-se2-sale-opt-tag]');
  if (tag instanceof HTMLElement) tag.hidden = !show;
  const was = option.querySelector('[data-se2-sale-opt-was]');
  if (was instanceof HTMLElement) {
    was.hidden = !show;
    if (show && base) {
      const text = wasSentence(was, base);
      if (was.textContent !== text) was.textContent = text;
    }
  }
}

/* ------------------------------------------------------------------------------------------------
   The button's words (audit 2026-09-29, FPDP-M-05). blocks/buy-buttons.liquid prints the Add to cart words in
   <span data-se2-atc-label> with both labels on it; this picks one for the chosen option. The last mode asked for
   is remembered per form, and a MutationObserver on the button's wrapper re-applies it whenever Horizon morphs the
   button back to the server's words (every size change), before the browser paints. A disabled button keeps the
   server's words: Sold out, or Unavailable.
   ------------------------------------------------------------------------------------------------ */

/** @type {WeakMap<HTMLFormElement, 'plan' | 'once'>} */
const LABEL_MODES = new WeakMap();
/** @type {WeakSet<Element>} */
const LABEL_WATCHED = new WeakSet();

/**
 * @param {HTMLFormElement | null} form
 * @param {'plan' | 'once'} [mode]
 */
function setAtcLabel(form, mode) {
  if (!form) return;
  if (mode) LABEL_MODES.set(form, mode);
  const label = form.querySelector('[data-se2-atc-label]');
  if (!(label instanceof HTMLElement)) return;
  watchAtcLabel(form, label);
  const planText = label.getAttribute('data-se2-label-plan');
  const onceText = label.getAttribute('data-se2-label-once');
  if (!planText || !onceText) return;
  const button = label.closest('button');
  if (button instanceof HTMLButtonElement && button.disabled) return;
  const want = LABEL_MODES.get(form) === 'plan' ? planText : onceText;
  if (label.textContent !== want) label.textContent = want;
}

/**
 * @param {HTMLFormElement} form
 * @param {HTMLElement} label
 */
function watchAtcLabel(form, label) {
  const wrap = label.closest('.se2-buy__atc') || label.closest('button');
  if (!wrap || LABEL_WATCHED.has(wrap) || !('MutationObserver' in window)) return;
  LABEL_WATCHED.add(wrap);
  new MutationObserver(() => setAtcLabel(form)).observe(wrap, {
    subtree: true,
    childList: true,
    characterData: true,
    attributes: true,
    attributeFilter: ['disabled'],
  });
}

/**
 * "Subscribe & save" after the plan price, and the struck price's "one-time", only while a plan is chosen.
 * @param {Element} root
 * @param {boolean} plan
 */
function setOfferQualifier(root, plan) {
  const qual = root.querySelector('[data-se2-offer-qual]');
  if (qual instanceof HTMLElement) qual.hidden = !plan;
}

/**
 * The "Sold out" tag beside the price (FPDP-B-02).
 * @param {Element} root
 * @param {boolean} available
 */
function setStockTag(root, available) {
  const tag = root.querySelector('[data-se2-stock-tag]');
  if (tag instanceof HTMLElement) tag.hidden = available;
}

/**
 * The diffuser head's "Sold out" tag beside the price (audit 2026-09-29, DPDP-B-02 part 5;
 * sections/se2-diffuser-pdp.liquid). Scoped to the diffuser buy box ([data-se2-dpdp3]), so the fragrance PDP's tag,
 * which paintVariant drives through setStockTag, is never read or written here.
 * @param {Element} section
 * @param {boolean} available
 */
function setDiffuserStockTag(section, available) {
  const tag = section.querySelector('[data-se2-dpdp3] [data-se2-stock-tag]');
  if (tag instanceof HTMLElement) tag.hidden = available;
}

/**
 * Hides (or shows) the purchase controls a sold-out size cannot use: the toggle slot, the note and the plan slot.
 * @param {Element} section
 * @param {boolean} hide
 */
function setPurchaseControlsHidden(section, hide) {
  for (const el of section.querySelectorAll('[data-se2-po-slot], [data-se2-po-note], [data-se2-plan-slot]')) {
    if (el instanceof HTMLElement && !el.closest('product-card, quick-add-dialog, dialog')) el.hidden = hide;
  }
}

/* ------------------------------------------------------------------------------------------------
   The live region (audit 2026-09-29, FPDP-B-17). One sentence per size or purchase-option change, written 400ms
   after the last paint so the instant paint and the re-fetch that follows it announce once, and written only when
   it differs from the last one (the fresh panel repeats what the paint already said).
   ------------------------------------------------------------------------------------------------ */

/** @type {WeakMap<Element, { last: string | null, timer: number }>} */
const LIVE_STATE = new WeakMap();

/**
 * @param {Element} root
 * @param {string} text
 * @param {boolean} [prime] record without announcing
 */
function announce(root, text, prime = false) {
  const region = root.querySelector('[data-se2-price-live]');
  if (!(region instanceof HTMLElement) || !text) return;
  let state = LIVE_STATE.get(region);
  if (!state) {
    state = { last: null, timer: 0 };
    LIVE_STATE.set(region, state);
  }
  if (prime) {
    state.last = text;
    return;
  }
  window.clearTimeout(state.timer);
  const current = state;
  current.timer = window.setTimeout(() => {
    if (text === current.last) return;
    current.last = text;
    region.textContent = text;
  }, 400);
}

/** The chosen size's name ("120 mL"), or '' on a product with no size picker. */
function sizeLabel(root) {
  const checked = root.querySelector('variant-picker input[type="radio"]:checked');
  return checked instanceof HTMLInputElement ? (checked.value || '').trim() : '';
}

/**
 * "120 mL, Subscribe & save: $39.99 every 30 days; one-time $49.99" · "120 mL, one-time: $49.99" · "50 mL: sold out"
 * @param {{ root?: Element, size: string, available: boolean, plan: boolean, price: string, once: string | null, cadence: string }} state
 */
function priceSentence({ root, size, available, plan, price, once, cadence }) {
  if (!available) {
    return size
      ? liveWords(root, 'se2MsgSoldOutSize', '[size]: sold out', { size })
      : liveWords(root, 'se2MsgSoldOut', 'Sold out');
  }
  if (plan) {
    const every = cadence ? ` ${liveWords(root, 'se2MsgEvery', 'every [cadence]', { cadence })}` : '';
    const oneTime =
      once && once !== price ? `; ${liveWords(root, 'se2MsgOneTimeAfter', 'one-time [price]', { price: once })}` : '';
    const head = size
      ? liveWords(root, 'se2MsgPlanSize', '[size], Subscribe & save: [price]', { size, price })
      : liveWords(root, 'se2MsgPlan', 'Subscribe & save: [price]', { price });
    return `${head}${every}${oneTime}`;
  }
  return size
    ? liveWords(root, 'se2MsgOnceSize', '[size], one-time: [price]', { size, price })
    : liveWords(root, 'se2MsgOnce', 'One-time: [price]', { price });
}

/* ------------------------------------------------------------------------------------------------
   Instant paint (2026-09-24) — see note 4 at the top.
   ------------------------------------------------------------------------------------------------ */

/**
 * `base` and `sale` (sale pricing, note 7): the regular price, and 1 when `price` is a sale price (0 otherwise).
 * @typedef {{ price: string, base?: string, sale?: number | boolean }} SaleFields
 * @typedef {SaleFields & { cents: number, available: boolean, title?: string, plans: Record<string, SaleFields & { pct: number, every?: string }> }} PriceEntry
 */

/** Parsed price map per section, so the JSON is read once. `null` means the section has none. */
const PRICE_MAPS = new WeakMap();

/**
 * @param {Element} section
 * @returns {Record<string, PriceEntry> | null}
 */
function priceMapFor(section) {
  if (PRICE_MAPS.has(section)) return PRICE_MAPS.get(section) ?? null;
  let map = null;
  const node = section.querySelector('[data-se2-price-map]');
  if (node) {
    try {
      const parsed = JSON.parse(node.textContent || '');
      map = parsed && typeof parsed.variants === 'object' ? parsed.variants : null;
    } catch {
      map = null;
    }
  }
  PRICE_MAPS.set(section, map);
  return map;
}

/** The variant id of the checked size radio in a section, or '' when there is no picker. */
function checkedVariantId(section) {
  const checked = section.querySelector('variant-picker input[type="radio"]:checked');
  return checked instanceof HTMLInputElement ? checked.dataset.variantId || '' : '';
}

/**
 * Paints price pair, in-button price and form state for a variant from the map — no network.
 * The mode (plan or one-time) is the shopper's remembered choice, else the merchant default the panel's
 * slot carries (one-time on an Offer-URL landing, as in #applyDefault); a variant with no allocation for that
 * plan paints its one-time price instead, exactly what the fresh panel will show when it lands (or fail to
 * render, for the 20 mL trial size).
 * @param {Element} section
 * @param {string} variantId
 * @param {PriceEntry} entry
 */
function paintVariant(section, variantId, entry) {
  const slot = section.querySelector('[data-se2-po-slot]');
  const panel = section.querySelector('[data-se2-purchase-options]');
  const key =
    (panel instanceof HTMLElement && panel.dataset.formBlock) ||
    (slot instanceof HTMLElement && slot.dataset.se2PoSlot) ||
    'default';
  const planIds = Object.keys(entry.plans || {});
  const saved = CHOICES.get(key);
  const defaultMode =
    (slot instanceof HTMLElement ? slot.dataset.se2PoDefault : null) ||
    (panel instanceof HTMLElement ? panel.dataset.default : null);
  const wantsPlan = saved ? saved.mode === 'plan' : defaultMode === 'plan' && !SE2_LANDED_ON_OFFER_URL;
  // A size that cannot be bought offers no plan (audit 2026-09-29, FPDP-B-02): its one-time price, a "Sold out" tag,
  // and none of the controls below; the remembered choice is kept for the next size that can be bought.
  const available = entry.available !== false;

  /** @type {string | null} */
  let planId = null;
  if (available && wantsPlan && planIds.length) {
    planId = saved?.plan && entry.plans[saved.plan] ? saved.plan : planIds[0];
  }

  // The paint may only promise a plan the FORM can post right now. Arriving from the 20 mL there is no
  // panel in the form yet — it comes with the fetch — so a tap on Add inside that window would be queued
  // without a selling plan and land as a one-time line. Until 2026-09-25 the paint therefore showed the
  // one-time price there, and the plan price replaced it only when the fetch landed, 1 to 4.6 seconds later
  // on DEV (review handoff). Now the form is given the plan itself: one hidden selling_plan input carrying
  // the plan the panel will default to, which Horizon's add path posts (assets/product-form.js reads the
  // enabled selling_plan field) and which the fresh panel withdraws on connect. Price and post agree from
  // the tap. A form that HAS plan selects but none holding this plan still falls back to the one-time price.
  const idInput = section.querySelector('form input[name="id"]');
  const form = idInput instanceof HTMLInputElement ? idInput.closest('form') : null;
  for (const interim of form?.querySelectorAll('input[data-se2-plan-interim]') ?? []) interim.remove();
  if (planId && form) {
    const selects = Array.from(form.querySelectorAll('select[data-se2-plan-select]'));
    const held = selects.some((select) =>
      select instanceof HTMLSelectElement && Array.from(select.options).some((option) => option.value === planId),
    );
    if (!held && selects.length === 0) {
      const interim = document.createElement('input');
      interim.type = 'hidden';
      interim.name = 'selling_plan';
      interim.value = planId;
      interim.setAttribute('data-se2-plan-interim', '');
      form.appendChild(interim);
    } else if (!held) {
      planId = null;
    }
  } else if (planId && !form) {
    planId = null;
  }
  const plan = planId ? entry.plans[planId] : null;
  const activePrice = plan ? plan.price : entry.price;

  // The plan the form now posts changed with the size (none on the 20 mL): the other controls hear it at once,
  // before the fresh panel connects and confirms it (b345 contract).
  const productEl = section.querySelector('[data-se2-purchase-options][data-product-id], product-form-component[data-product-id]');
  document.dispatchEvent(
    new CustomEvent('se2:plan-change', {
      detail: {
        productId: productEl instanceof HTMLElement ? productEl.dataset.productId || '' : '',
        sellingPlanId: planId,
        source: 'size',
      },
    }),
  );
  const discount = !!plan && plan.pct > 0;

  section.querySelector('.se2-fpdp3__hero, .se2-dpdp3__hero')?.classList.add('is-live');

  // The two price lines (2026-11-04). The line under the price names the other way to buy: the one-time price under
  // a plan; the first plan under One-time; "One-time only" for a size with no plan, "One-time" for a sold-out one.
  const firstPlan = planIds.length ? entry.plans[planIds[0]] : null;
  let meta;
  if (plan) meta = priceWords(section, 'se2WOrOnce', 'or [price] one-time', { price: entry.price });
  else if (!planIds.length) meta = priceWords(section, 'se2WOnceOnly', 'One-time only');
  else if (!available) meta = priceWords(section, 'se2WOnce', 'One-time');
  else {
    meta = priceWords(section, 'se2WOrPlan', 'or [price] [every] with Subscribe\u00a0&\u00a0save', {
      price: firstPlan?.price || '',
      every: firstPlan?.every || '',
    });
  }
  paintPriceLines(section, { price: activePrice, every: plan ? plan.every || '' : '', meta });

  // The cards' prices follow at once too; the fresh panel from the re-fetch replaces them a moment later.
  for (const card of section.querySelectorAll('[data-se2-purchase-options] [data-se2-option]')) {
    const cardPrice = card.querySelector('[data-se2-po-price]');
    if (!cardPrice) continue;
    if (card.getAttribute('data-se2-option') === 'once') {
      cardPrice.textContent = entry.price;
      paintOptionSale(card, Boolean(entry.sale), entry.base || '');
      continue;
    }
    const select = card.querySelector('select[data-se2-plan-select]');
    const own = select instanceof HTMLSelectElement ? entry.plans[select.value] : null;
    if (own) {
      cardPrice.textContent = own.price;
      paintOptionSale(card, Boolean(own.sale), own.base || '');
    }
  }

  // Sale pricing: the tag beside the price follows the size and the plan the form now posts.
  // A sold-out size shows its "Sold out" tag alone.
  paintSale(section, available && (plan ? Boolean(plan.sale) : Boolean(entry.sale)), (plan ? plan.base : entry.base) || '');

  const price = section.querySelector('[data-se2-active-price]');
  if (price) price.textContent = activePrice;

  const compare = section.querySelector('[data-se2-active-compare]');
  if (compare instanceof HTMLElement) {
    compare.hidden = !discount;
    const value = compare.querySelector('[data-se2-active-compare-value]');
    if (value) value.textContent = entry.price;
  }
  setOfferQualifier(section, !!plan);
  setStockTag(section, available);
  setPurchaseControlsHidden(section, !available);

  // A variant without the offer must not keep showing the old variant's "20% OFF · every 30 days" line
  // while the fetch is in flight. A variant WITH the offer keeps whatever is drawn; the fresh panel
  // rewrites it on connect.
  if (!plan) {
    for (const selector of ['[data-se2-plan-line]', '[data-se2-plan-benefits]']) {
      const el = section.querySelector(selector);
      if (el instanceof HTMLElement) el.hidden = true;
    }
  }

  if (idInput instanceof HTMLInputElement) idInput.value = variantId;
  if (available) {
    setAtcPrice(form, activePrice);
    setAtcLabel(form, plan ? 'plan' : 'once');
  }

  // Selling-plan safety for a tap on Add to cart inside the gap: only a plan the new variant is allocated
  // may stay enabled, and every other <select name="selling_plan"> is disabled so nothing the variant
  // cannot honour is posted (that 422 was finding V-01).
  if (form) {
    for (const select of form.querySelectorAll('select[data-se2-plan-select]')) {
      if (!(select instanceof HTMLSelectElement)) continue;
      const holds = !!planId && Array.from(select.options).some((option) => option.value === planId);
      if (holds && planId) {
        select.value = planId;
        select.disabled = false;
      } else {
        select.disabled = true;
      }
    }
  }

  // One sentence for the live region. The cadence is the plan's own ("30 days"), read from the option that
  // carries it in the form, else from the line on screen.
  let cadence = '';
  if (planId && form) {
    const option = form.querySelector(`select[data-se2-plan-select] option[value="${CSS.escape(planId)}"]`);
    cadence = (option instanceof HTMLOptionElement && (option.dataset.se2FreqBare || option.dataset.se2FreqShort)) || '';
  }
  if (planId && !cadence) cadence = section.querySelector('[data-se2-plan-current]')?.textContent?.trim() || '';
  announce(
    section,
    priceSentence({
      root: section,
      size: sizeLabel(section),
      available,
      plan: !!plan,
      price: activePrice,
      once: entry.price,
      cadence,
    }),
    false,
  );
}

/**
 * The size radio's change, heard in the capture phase so it runs before Horizon's own variant-picker handler
 * starts the fetch. Cards and quick-add dialogs have their own price handling and are left alone.
 * @param {Event} event
 */
function onVariantChange(event) {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || input.type !== 'radio' || !input.checked) return;
  if (!input.closest('variant-picker') || input.closest('product-card, quick-add-dialog, dialog')) return;
  const section = input.closest('.shopify-section');
  if (!section) return;
  // The diffuser's tag follows the tapped colour at once: with one option, the value's availability is the
  // variant's. The fetched variant settles it in onProductSelect (step 4b).
  const optionAvailable = input.dataset.optionAvailable;
  if (optionAvailable === 'true' || optionAvailable === 'false') {
    setDiffuserStockTag(section, optionAvailable === 'true');
  }
  const map = priceMapFor(section);
  const variantId = input.dataset.variantId || '';
  const entry = map && map[variantId];
  if (!entry) return;
  markPrimed(section);
  paintVariant(section, variantId, entry);
}

/**
 * After a variant change, refresh the in-button price and the purchase-options panel from the fetched section HTML.
 * @param {Event & { promise?: Promise<any> }} event
 */
function onProductSelect(event) {
  const promise = event.promise;
  if (!promise || typeof promise.then !== 'function') return;

  const target = event.target;
  if (!(target instanceof Element)) return;
  if (target.closest('product-card, quick-add-dialog, dialog')) return;

  const section = target.closest('.shopify-section');
  if (!section) return;

  promise
    .then(({ detail }) => {
      const html = detail?.html;
      if (!(html instanceof Document)) return;

      // Horizon aborts the previous fetch when a new size is picked, but a response that was already in
      // hand when the shopper tapped again is not aborted: it describes a size that is no longer chosen.
      // Swapping it in would paint the wrong price and enable the wrong plan, so it is dropped — the fetch
      // for the current size lands next and does the swap. The fetched variant is `detail.resource` (the
      // variant JSON assets/variant-picker.js resolves with; the event's own `variantId` never reaches
      // the promise), and the check is repeated inside the deferred task, which is when it matters.
      const fetched = detail?.resource?.id != null ? String(detail.resource.id) : '';
      const stale = () => {
        const current = checkedVariantId(section);
        return fetched !== '' && current !== '' && fetched !== current;
      };
      if (stale()) return;

      // Let the theme's own listeners (same promise) finish their morphs first.
      setTimeout(() => {
        if (stale()) return;
        // 1. in-button price: replace, insert or remove (absent when the variant cannot be added). Only the price
        //    that sits BESIDE the button: the inline one is inside the button, which Horizon has just morphed —
        //    and that morph MOVES nodes it has to add out of the fetched document, so after a sold-out size (a
        //    button with no price) the fetched copy is gone and this step used to remove the live price the morph
        //    had just put in (audit 2026-09-29, found on the sold-out → 120 mL path). The panel below writes it.
        for (const wrap of section.querySelectorAll('.se2-buy__atc')) {
          const current = wrap.querySelector(':scope > [data-se2-atc-price]');
          const fresh = html.querySelector('.se2-buy__atc > [data-se2-atc-price]');
          if (fresh) {
            const node = document.importNode(fresh, true);
            if (current) current.replaceWith(node);
            else wrap.appendChild(node);
          } else {
            current?.remove();
          }
        }

        // 1b. The purchase controls a sold-out size does not render (audit 2026-09-29, FPDP-B-02): hidden when
        //     the fetched HTML has none, re-inserted (toggle slot, note, plan slot, in that order, before the
        //     buttons) when it has them and the page lost them — a ?variant= landing on a sold-out size.
        syncPurchaseControls(section, html);

        // 2. purchase options panel: the new panel restores the remembered choice on connect and rewrites the
        //    section's price pair and plan line from the new variant's data.
        //    Driven off the SLOT, not the panel, because the panel is absent on a variant with no allocation
        //    (the 20 mL trial size is deliberately one-time only). Keying on the panel meant a variant change
        //    that removed it left nothing to match on, so subscribing again after visiting the 20 mL was
        //    impossible without a reload. The slot always renders, so both directions work.
        for (const slot of section.querySelectorAll('[data-se2-po-slot]')) {
          const key = slot instanceof HTMLElement ? slot.dataset.se2PoSlot || '' : '';
          const fresh = html.querySelector(`[data-se2-po-slot="${CSS.escape(key)}"]`);
          const next = fresh?.querySelector('[data-se2-purchase-options]');
          const current = slot.querySelector('[data-se2-purchase-options]');

          if (next) {
            const node = document.importNode(next, true);
            if (current) current.replaceWith(node);
            else slot.appendChild(node);
          } else {
            current?.remove();
          }
        }

        // 3. With no panel to write it, the pair still has to say the fetched variant's price: the instant
        //    paint already did, and this keeps it honest if that paint was skipped (no map, no radio).
        if (!section.querySelector('[data-se2-purchase-options]')) {
          const map = priceMapFor(section);
          const id = checkedVariantId(section);
          const entry = map && id ? map[id] : null;
          if (entry) paintVariant(section, id, entry);
        }

        // 4. The sold-out next step (#se2-restock) and the reassurance row follow the variant: neither is part of
        //    Horizon's morph, and "Ships in 1 day" is a stock promise (audit 2026-09-29, DPDP-B-02).
        swapFromFetched(section, html, '[data-se2-restock-slot]');
        swapFromFetched(section, html, '[data-testid="se2-reassure"]');
        // The SCENTPRO row's offer line names the chosen size or the free one (2026-11-04).
        swapFromFetched(section, html, '[data-se2-pair-row]');

        // 4b. The diffuser head's "Sold out" tag (DPDP-B-02 part 5): the fetched variant decides, else the fetched tag.
        const resource = detail?.resource;
        if (resource && typeof resource.available === 'boolean') {
          setDiffuserStockTag(section, resource.available);
        } else {
          const freshTag = html.querySelector('[data-se2-dpdp3] [data-se2-stock-tag]');
          if (freshTag instanceof HTMLElement) setDiffuserStockTag(section, freshTag.hidden);
        }

        // 5. The button's words, after Horizon's morph put the server's back (the observer usually got there first).
        const form = section.querySelector('form[data-type="add-to-cart-form"]');
        if (form instanceof HTMLFormElement) setAtcLabel(form);
      }, 0);
    })
    .catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[se2-pdp-purchase] product select promise rejected:', error);
    });
}

/**
 * Replaces the section's copy of `selector` with the fetched one (both must exist). Plain markup only: nothing in
 * these nodes needs a script to run.
 * @param {Element} section
 * @param {Document} html
 * @param {string} selector
 */
function swapFromFetched(section, html, selector) {
  const current = section.querySelector(selector);
  const fresh = html.querySelector(selector);
  if (!current || !fresh || current.closest('product-card, quick-add-dialog, dialog')) return;
  current.replaceWith(document.importNode(fresh, true));
}

/**
 * Keeps the toggle slot, the note and the plan slot in step with the fetched section: hidden (and the panel
 * removed) when the server left them out for a sold-out size, shown again or inserted when it printed them.
 * @param {Element} section
 * @param {Document} html
 */
function syncPurchaseControls(section, html) {
  const buttons = section.querySelector('.product-form-buttons');
  const atc = buttons?.querySelector(':scope > .se2-buy__atc');
  if (!buttons || !atc) return;
  // The add error sits directly above the button (blocks/buy-buttons, QA 2026-10-06, SW-05): the controls go in
  // above it, so it is never parted from the button it is about.
  const before = atc.previousElementSibling;
  const anchor = before?.matches('.product-form-text__error') ? before : atc;
  for (const selector of ['[data-se2-po-slot]', '[data-se2-po-note]', '[data-se2-plan-slot]']) {
    const fresh = html.querySelector(`.product-form-buttons > ${selector}`);
    const current = buttons.querySelector(`:scope > ${selector}`);
    if (fresh && current instanceof HTMLElement) {
      current.hidden = false;
    } else if (fresh && !current) {
      const node = document.importNode(fresh, true);
      // The panel inside a fresh toggle slot is swapped in by step 2, which finds the slot empty and appends it.
      if (selector === '[data-se2-po-slot]') node.querySelector('[data-se2-purchase-options]')?.remove();
      buttons.insertBefore(node, anchor);
    } else if (!fresh && current instanceof HTMLElement) {
      current.hidden = true;
      if (selector === '[data-se2-po-slot]') current.querySelector('[data-se2-purchase-options]')?.remove();
    }
  }
}

/**
 * "120 mL: 2.4× the oil for $10 more" (FPDP-B-13): selects that size by clicking its radio, so Horizon's own
 * variant change runs. Delegated, because the variant picker is re-rendered on every size change.
 * @param {MouseEvent} event
 */
function onUpsellClick(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const button = target.closest('[data-se2-size-upsell]');
  if (!(button instanceof HTMLElement)) return;
  const picker = button.closest('variant-picker');
  const id = button.getAttribute('data-se2-size-upsell') || '';
  const radio = picker?.querySelector(`input[type="radio"][data-variant-id="${CSS.escape(id)}"]`);
  if (radio instanceof HTMLInputElement && !radio.checked) {
    event.preventDefault();
    radio.click();
    radio.focus({ preventScroll: true });
  }
}

/**
 * "Switch to 120 mL" under a sold-out size (FPDP-B-02, [data-se2-restock-switch]): selects that size in place by
 * clicking its radio, as the upsell line does, instead of following the ?variant= link. Following it reloaded the
 * page at the top and, because ?variant= marks an Offer-URL landing (SE2_LANDED_ON_OFFER_URL), opened it on
 * One-time; in place, CHOICES restores the shopper's option on the fresh panel. The picker is brought into view
 * (instantly under prefers-reduced-motion) only when it is not already on screen. A modified click, or a page
 * whose picker has no radio for that variant, keeps the link, which is also the no-JavaScript path.
 * @param {MouseEvent} event
 */
function onRestockSwitchClick(event) {
  if (event.defaultPrevented || event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const target = event.target;
  if (!(target instanceof Element)) return;
  const link = target.closest('a[data-se2-restock-switch]');
  if (!(link instanceof HTMLAnchorElement)) return;
  let id = '';
  try {
    id = new URL(link.href, window.location.href).searchParams.get('variant') || '';
  } catch {
    return;
  }
  if (!id) return;
  const scope = link.closest('.shopify-section') ?? document;
  const radio = Array.from(
    scope.querySelectorAll(`variant-picker input[type="radio"][data-variant-id="${CSS.escape(id)}"]`),
  ).find((input) => !input.closest('product-card, quick-add-dialog, dialog'));
  if (!(radio instanceof HTMLInputElement)) return;
  event.preventDefault();
  if (!radio.checked) radio.click();
  const picker = radio.closest('variant-picker');
  if (picker instanceof HTMLElement) {
    const box = picker.getBoundingClientRect();
    const header = document.querySelector('header-component, #header-component');
    const top = header instanceof HTMLElement ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
    if (box.top < top || box.bottom > window.innerHeight) {
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      picker.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
    }
  }
  radio.focus({ preventScroll: true });
}

/* ------------------------------------------------------------------------------------------------
   Rise in turn, once (2026-11-04; motion M8 and M6 of the boards). [data-se2-rise] marks a list whose children rise
   one after another the first time it comes into view and then hold: Smells like (sections/se2-fragrance-pdp), the
   composition tiles (sections/se2-fragrance-composition) and the kit card's FREE tag, which takes one sweep
   (snippets/se2-kit-card). The script only arms and fires: data-se2-rise="wait" until the list is on screen, then
   "go"; assets/se2-fpdp.css draws both, only under html.se2-motion and never inside [data-se2-motion-paused]. Nothing
   is armed under reduced motion or without IntersectionObserver, so the lists simply stand. Three notes take about
   2.3 s, well inside the 5 s the contract allows without a pause control.
   ------------------------------------------------------------------------------------------------ */
const RISE_REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

/** @type {IntersectionObserver | null} */
let riseObserver = null;

/** @param {ParentNode} scope */
function armRise(scope) {
  if (RISE_REDUCED.matches || !('IntersectionObserver' in window)) return;
  if (!riseObserver) {
    riseObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.setAttribute('data-se2-rise', 'go');
          riseObserver?.unobserve(entry.target);
        }
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.2 },
    );
  }
  for (const list of scope.querySelectorAll('[data-se2-rise=""]')) {
    list.setAttribute('data-se2-rise', 'wait');
    riseObserver.observe(list);
  }
}

if (!customElements.get('se2-purchase-options')) {
  customElements.define('se2-purchase-options', Se2PurchaseOptions);
}

// Sale pricing: assets/se2-sale.js has put a page's regular prices back (the sale ended while it was open, or the page
// came from a cache). The maps are read again on the next size change and every panel repaints from its cards now.
document.addEventListener('se2:sale-end', () => {
  for (const node of document.querySelectorAll('[data-se2-price-map]')) {
    const section = node.closest('.shopify-section');
    if (section) PRICE_MAPS.delete(section);
  }
  const announce = document.readyState === 'complete';
  for (const panel of document.querySelectorAll('se2-purchase-options')) {
    if (panel instanceof Se2PurchaseOptions && panel.isConnected) panel.refresh(announce);
  }
});

document.addEventListener(StandardEvents.productSelect, onProductSelect);
document.addEventListener('change', onVariantChange, true);
document.addEventListener('click', onUpsellClick);
document.addEventListener('click', onRestockSwitchClick);

// From the first paint the pair is the one price on the page wherever a map exists: the stylesheet hides
// Horizon's own price under .is-live, so the shopper never sees two figures disagree for a frame.
for (const node of document.querySelectorAll('[data-se2-price-map]')) {
  node.closest('.se2-fpdp3__hero, .se2-dpdp3__hero')?.classList.add('is-live');
}

armRise(document);

// The desktop buy box sticks beside the gallery with a top that knows the column's height (assets/se2-fpdp.css,
// audit 2026-09-25 desk-fbuy): a column taller than the viewport pins its BOTTOM — the buttons — instead of
// its top. The height is published as --se2-info-h and follows the subscription panel opening and closing.
// Since the 2026-09-25 review it is the height of the BUY part only — the column's top down to the ADD TO CART
// button — not the whole column: the four detail rows under it made the column taller than the viewport, and
// pinning its bottom slid the title, price and sizes under the header (1,161px at 1366x768, top -417px). Now the
// top stays at the header clearance whenever title-to-button fits; the reassurance row shows whenever there is room
// for it too, and the detail rows run on below the fold and arrive as the gallery ends.
// Below 990px the column has no box (display: contents) and reports 0, so the property is withdrawn and the
// stylesheet's no-JS rule stands on its own.
const se2Info = document.querySelector('.se2-fpdp3__info');
if (se2Info instanceof HTMLElement && 'ResizeObserver' in window) {
  const publishInfoHeight = () => {
    const height = se2Info.offsetHeight;
    if (height <= 0) {
      se2Info.style.removeProperty('--se2-info-h');
      return;
    }
    let pinned = height;
    const end =
      se2Info.querySelector('.se2-fpdp3__purchase .add-to-cart-button') ||
      se2Info.querySelector('.se2-fpdp3__purchase');
    if (end instanceof HTMLElement) {
      const reach = end.getBoundingClientRect().bottom - se2Info.getBoundingClientRect().top;
      if (reach > 0 && reach < height) pinned = Math.ceil(reach);
    }
    se2Info.style.setProperty('--se2-info-h', `${pinned}px`);
  };
  const observer = new ResizeObserver(publishInfoHeight);
  observer.observe(se2Info);
  const buy = se2Info.querySelector('.se2-fpdp3__purchase');
  if (buy instanceof HTMLElement) observer.observe(buy);
}
